import { getEntriesOf, getMutableCollection } from './collectionsRead';
import { DestinationPosition, MfsCollectionId, MfsItemId, SourcePosition } from '../../store/mfsTypes';
import { Change, NodeId, TreeView, SharedTree } from '@fluid-experimental/tree';
import { ChangesAndIds, CollectionIdInternal, getCollectionNode } from '.';

import { assertPrecondition, Result, mfsError } from '../../errors';
import { isAncestorOf } from './lookup';
import { assert, logVerbose } from '../../log';

/** Inserts the `itemIds` to the collection. */
export function insertToCollection(
  tree: SharedTree,
  view: TreeView,
  collectionId: MfsCollectionId,
  itemIds: MfsItemId[],
  position?: DestinationPosition
): ChangesAndIds<MfsItemId> {
  const collectionNode = getCollectionNode(tree, view, collectionId);
  const collection = getMutableCollection(tree, view, collectionNode.identifier);
  return collection.insert(position ?? { place: 'last' }, itemIds);
}

/**
 * Removes the element at the given position, from the given collection. No-op if the removed position is not already
 * element of the given collection.
 */
export function removeFromCollection(
  tree: SharedTree,
  view: TreeView,
  collectionId: MfsCollectionId,
  position: SourcePosition
): Change | undefined {
  const collectionNode = getCollectionNode(tree, view, collectionId);
  const collection = getMutableCollection(tree, view, collectionNode.identifier);
  const change = collection.remove(position.elementId);
  logVerbose(
    'removeFromCollection',
    `${change ? '' : 'skip'} removing ${JSON.stringify(position)}, collectionId:${collectionNode.identifier}`
  );
  return change;
}

/**
 * Moves the collection item from the given source position to the destination position. The destination might be in a
 * different collection or the same.
 */
export function moveCollectionItem(
  tree: SharedTree,
  view: TreeView,
  collectionId: MfsCollectionId,
  from: SourcePosition,
  to: DestinationPosition,
  destinationCollectionId?: MfsCollectionId
): Change[] {
  const collectionNode = getCollectionNode(tree, view, collectionId);
  const destinationCollectionNode = destinationCollectionId
    ? getCollectionNode(tree, view, destinationCollectionId)
    : undefined;
  // if moving to a different collection, make sure it also belongs to the given app.
  assertPrecondition(!destinationCollectionId || !!destinationCollectionNode);

  // validate the move positions are well defined and does not form a cycle.
  assert(() => canMove(view, from, to));

  const collection = getMutableCollection(tree, view, collectionNode.identifier);

  logVerbose(
    'moveCollectionItem',
    `Moving ${JSON.stringify(from)}, to: ${JSON.stringify(to)}, collectionId:${collectionId} ${
      destinationCollectionId ? ', toCollectionId' : ''
    }${destinationCollectionId ?? ''}`
  );

  return collection.move(from, to, destinationCollectionNode?.identifier);
}

/** Checks whether the from and to positions are different and `from` is not an ancestor of `to`. */
export function canMove(view: TreeView, from: SourcePosition, to: DestinationPosition): Result {
  const getSecondPosition = (to: DestinationPosition): NodeId | undefined => {
    if (to.place === 'before' || to.place === 'under' || to.place === 'after') {
      return to.elementId;
    }
    assertPrecondition(to.place === 'first' || to.place === 'last');
    return to.parentId;
  };

  const firstPos = from.elementId;
  const secondPos = getSecondPosition(to);

  if (!secondPos) {
    // moving to the first or last position is always OK.
    return Result.ok();
  }

  // if there's a second position relative to an element, make sure that the moved element is not ancestor of it.
  return firstPos === secondPos || isAncestorOf(view, firstPos, secondPos)
    ? Result.fail(mfsError.invalidMove())
    : Result.ok();
}

export function removeItemById(
  tree: SharedTree,
  view: TreeView,
  collectionNodeId: CollectionIdInternal,
  itemId: MfsItemId
): Change[] {
  const collection = getMutableCollection(tree, view, collectionNodeId.collectionNodeId);
  const changes: Change[] = [];

  // Find and remove all occurences of given item id in the collection.
  for (const entry of getEntriesOf(tree, view, itemId, collectionNodeId)) {
    logVerbose(
      'removeItemById',
      `Removing '${JSON.stringify(entry)}' from collection ${JSON.stringify(collectionNodeId)}`
    );
    const removeChange = collection.remove(entry.elementId);
    if (removeChange) {
      changes.push(removeChange);
    }
  }

  return changes;
}

/** Clear all elements of the specified collection or sub-collection. */
export function clearCollection(
  tree: SharedTree,
  view: TreeView,
  { collectionNodeId: id, parentId }: CollectionIdInternal
): Change[] {
  const collection = getMutableCollection(tree, view, id);
  logVerbose(
    'clearCollection',
    `Clearing ${parentId ? 'sub' : ''} collection id:${id} ${parentId ? 'parentId:' : ''}${parentId}`
  );
  return collection.clear(parentId);
}
